// =============================================================================
// src/features/sentinel-live-hydrate.ts — Hidrata Copilot, Crise e Reputação com Sentinel.
// =============================================================================

import type { SentinelClient } from '../data/sentinel-client';
import {
  buildCampaignPriorities,
  buildCopilotOverlay,
  buildCrisisLiveActions,
  buildCrisisLivePatch,
  buildCrisisVolumeSeries,
  buildReputationPatch,
  buildTacticsBySection,
} from '../data/sentinel-overlay';
import { debugLiveIntegration } from '../data/live-debug';
import { applyCopilotLiveOverlay } from './copilot-chat/copilot-data-store';
import { applyReputationLivePatch } from './reputation/reputation-live-store';
import { applyCrisisLiveActions, applyCrisisLivePatch, applyCrisisLiveSeries } from './crisis/crisis-live-store';
import { applyCampaignPriorities, applyTacticsLiveOverride } from '../chrome/copilot-sidebar/tactics-live-store';

export type SentinelLiveSource = 'live' | 'static';

export interface SentinelLiveStatus {
  readonly source: SentinelLiveSource;
}

let hydration: Promise<SentinelLiveStatus> | null = null;
let resolvedSource: SentinelLiveSource = 'static';

export function hydrateSentinelLive(client: SentinelClient | null): Promise<SentinelLiveStatus> {
  if (!hydration) hydration = runHydration(client);
  return hydration;
}

export function sentinelLiveSource(): SentinelLiveSource {
  return resolvedSource;
}

async function runHydration(client: SentinelClient | null): Promise<SentinelLiveStatus> {
  if (!client) {
    debugLiveIntegration('sentinel-live', 'fallback_static', { reason: 'client_null' });
    return { source: 'static' };
  }
  try {
    const dashboard = await client.fetchDashboard();
    applyCopilotLiveOverlay(buildCopilotOverlay(dashboard));
    applyReputationLivePatch(buildReputationPatch(dashboard));
    // (patch do briefing removido 2026-07-28 — o briefing agora le o social_listening)
    const crisisPatch = buildCrisisLivePatch(dashboard);
    applyCrisisLivePatch(crisisPatch);
    if (!crisisPatch.series) {
      // sem crise ativa (sem série horária real) → série fabricada do sentimento (fallback).
      const fallback = buildCrisisVolumeSeries(dashboard);
      if (fallback) applyCrisisLiveSeries(fallback);
    }
    applyCrisisLiveActions(buildCrisisLiveActions(dashboard));
    const tactics = buildTacticsBySection(dashboard);
    applyTacticsLiveOverride(tactics);
    const priorities = buildCampaignPriorities(dashboard);
    applyCampaignPriorities(priorities);
    resolvedSource = 'live';
    debugLiveIntegration('sentinel-live', 'live_dashboard', {
      feed: dashboard.feed.length,
      alerts: dashboard.alerts.length,
      actionRecommendations: dashboard.actionRecommendations.length,
      tactics: Object.keys(tactics).length,
      priorities: priorities.length,
      hasSentiment: dashboard.sentiment !== null,
      hasReputation: dashboard.reputation !== null,
    });
    return { source: 'live' };
  } catch (error) {
    debugLiveIntegration('sentinel-live', 'fallback_static', {
      reason: 'fetch_dashboard_failed',
      error: error instanceof Error ? error.message : String(error),
    });
    return { source: 'static' };
  }
}

export function __resetSentinelLiveForTests(): void {
  hydration = null;
  resolvedSource = 'static';
}
